import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  PropsWithChildren,
} from 'react'
import Snackbar from '@mui/material/Snackbar'
import Alert, { AlertColor } from '@mui/material/Alert'

type Severidade = AlertColor // 'success' | 'info' | 'warning' | 'error'

interface NotificacaoState {
  aberto: boolean
  message: string
  severidade: Severidade
  time?: number
}

interface NotificationContextType {
  notify: (message: string, severidade?: Severidade, time?: number) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
)

const ESTADO_INICIAL: NotificacaoState = {
  aberto: false,
  message: '',
  severidade: 'info',
  time: 5000
}

export function NotificationProvider({ children }: PropsWithChildren) {
  const [estado, setEstado] = useState<NotificacaoState>(ESTADO_INICIAL)
  const { aberto, time, severidade, message } = estado

  const notify = useCallback(
    (message: string, severidade: Severidade = 'info', time: number = severidade === 'error' ? 10000 : 5000) => {
      setEstado({ aberto: true, message, severidade, time })
    },
    []
  )

  const fechar = useCallback((_?: unknown, motivo?: string) => {
    if (motivo === 'clickaway') return
    setEstado((prev) => ({ ...prev, aberto: false }))
  }, [])

  const value = useMemo(() => ({ notify }), [notify])

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={aberto}
        autoHideDuration={time}
        onClose={fechar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={fechar}
          severity={severidade}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  )
}

export function useNotification() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error(
      'useNotification deve ser usado dentro de NotificationProvider'
    )
  }
  return context
}